/**
 * The slot ADDRESS ↔ wire-key codec (feature 0059 lot 1d).
 *
 * A TextSlot's address is `[sectionId, ...blockIds] + fieldId` (ui-react, stable nanoids). The backend
 * treats the key as an opaque ≤200-char string and echoes it back — this file is the only place that
 * knows the encoding, so the two sides can never drift apart by accident.
 *
 * `/` joins path segments and `#` separates the fieldId. Both are safe: nanoids draw from
 * [A-Za-z0-9_-] and field ids are code identifiers — but decode is defensive anyway (the values come
 * back from a network round-trip through a language model's output).
 */
import type { TextPatch, TextSlot } from '@aquilixshop/ui-react';
import type { AiSlotDto } from '../../lib/aiApi';

/** Backend-validated caps (AiController DTOs) — exceeding them is a 400, so clamp here. */
const MAX_KEY = 200;
const MAX_LABEL = 120;
const MAX_CURRENT = 2000;

export function encodeSlotKey(slot: Pick<TextSlot, 'path' | 'fieldId'>): string {
  return `${slot.path.join('/')}#${slot.fieldId}`;
}

/** Inverse of {@link encodeSlotKey}. Null for anything malformed — a dropped value, never a throw. */
export function decodeSlotKey(key: string): { path: string[]; fieldId: string } | null {
  const hash = key.lastIndexOf('#');
  if (hash <= 0 || hash === key.length - 1) return null;
  const path = key.slice(0, hash).split('/').filter(Boolean);
  if (path.length === 0) return null;
  return { path, fieldId: key.slice(hash + 1) };
}

/** A slot as the wire wants it — clamped to the server's validation caps. */
export function toSlotDto(slot: TextSlot): AiSlotDto | null {
  const key = encodeSlotKey(slot);
  if (key.length > MAX_KEY) return null; // pathological nesting — skip the slot, not the section
  return {
    key,
    label: slot.label.slice(0, MAX_LABEL),
    kind: slot.kind,
    maxLength: slot.maxLength,
    current: slot.current.slice(0, MAX_CURRENT),
  };
}

/**
 * Model values (keyed by wire key) → the patches applyTextSlots consumes. Unknown or malformed keys
 * are silently dropped HERE only in shape — applyTextSlots re-verifies every address against the
 * document and reports its own rejects, so nothing lands unchecked.
 */
export function valuesToPatches(values: Record<string, string>): TextPatch[] {
  const patches: TextPatch[] = [];
  for (const [key, value] of Object.entries(values)) {
    const decoded = decodeSlotKey(key);
    if (!decoded || typeof value !== 'string') continue;
    patches.push({ path: decoded.path, fieldId: decoded.fieldId, value });
  }
  return patches;
}
